import React from 'react';
import { AlertTriangle, RefreshCw, Home } from 'lucide-react';

export default class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, info) {
    console.error('[ErrorBoundary]', error, info.componentStack);
  }

  handleReset = () => {
    this.setState({ hasError: false, error: null });
    this.props.onReset?.();
  };

  render() {
    if (!this.state.hasError) return this.props.children;

    const { inline, message } = this.props;

    if (inline) {
      return (
        <div className="flex flex-col items-center justify-center gap-2 py-8 text-sm"
          style={{ color: 'rgba(255,255,255,0.45)' }}>
          <AlertTriangle size={16} style={{ color: '#f59e0b' }} />
          <span>{message || 'This section failed to load.'}</span>
          <div className="flex items-center gap-3 mt-1">
            <button
              onClick={this.handleReset}
              className="flex items-center gap-1 underline underline-offset-2 text-xs"
              style={{ color: '#0ea5e9' }}
            >
              <RefreshCw size={11} /> Retry
            </button>
            <span style={{ color: 'rgba(255,255,255,0.2)' }}>·</span>
            <a
              href="/"
              className="flex items-center gap-1 underline underline-offset-2 text-xs"
              style={{ color: 'rgba(255,255,255,0.45)' }}
            >
              <Home size={11} /> Go Home
            </a>
          </div>
        </div>
      );
    }

    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-4 px-6"
        style={{ background: '#0B0F1A' }}>
        <AlertTriangle size={40} style={{ color: '#f59e0b' }} />
        <div className="text-center">
          <p className="text-base font-semibold mb-1" style={{ color: '#fff' }}>
            Something went wrong
          </p>
          <p className="text-sm" style={{ color: 'rgba(255,255,255,0.45)' }}>
            {message || 'An unexpected error occurred. Please reload the app.'}
          </p>
        </div>
        <div className="flex items-center gap-3 mt-2">
          <button
            onClick={() => window.location.reload()}
            className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold"
            style={{ background: '#0ea5e9', color: '#fff' }}
          >
            <RefreshCw size={14} /> Reload App
          </button>
          <a
            href="/"
            className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold"
            style={{ background: 'rgba(255,255,255,0.08)', color: 'rgba(255,255,255,0.7)' }}
          >
            <Home size={14} /> Go Home
          </a>
        </div>
      </div>
    );
  }
}

// Convenience wrapper for inline (per-section) boundaries
export function PageErrorBoundary({ children, message }) {
  return (
    <ErrorBoundary inline message={message}>
      {children}
    </ErrorBoundary>
  );
}
